import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useAppSelector } from '../../hooks/useStore'
import {
  getExtrasInSeasons,
  getPaginatedEpisodes,
  getSeasonDetails,
} from '../../actions/contentActions'
import {
  getSection,
  handleExtrasInSeasonsError,
  handlePaginatedEpisodesError,
  handleSeasonDetailsError,
} from './utils'

import type { Season } from '../../reducers/types/reducerTypes'
import type { SeasonContentParams, SeasonType, VideoSection } from './types'

const useSeasonContent = (
  activeSeason: Season | undefined,
  type: SeasonType | undefined
): SeasonContentParams => {
  const stationId = useAppSelector((state) => state.station.station_id)
  const [videoSections, setVideoSections] = useState<VideoSection[]>([])
  const [seasonDescription, setSeasonDescription] = useState<
    string | undefined
  >(undefined)
  const [loadingSections, setLoadingSections] = useState<boolean>(true)
  const [loadingDescription, setLoadingDescription] = useState<boolean>(true)
  const { show: showSlug } = useParams()

  useEffect(() => {
    if (activeSeason && showSlug && stationId) {
      setLoadingSections(true)
      const promises: Promise<void>[] = []
      if (type === 'all') {
        promises.push(getEpisodes())
      }
      promises.push(getExtras())
      Promise.all(promises).finally(() => setLoadingSections(false))
    }
    return () => {
      setVideoSections([])
      setSeasonDescription(undefined)
      setLoadingSections(true)
      setLoadingDescription(true)
    }
  }, [activeSeason, showSlug, stationId, type])

  useEffect(() => {
    if (activeSeason) {
      setLoadingDescription(true)
      getSeasonDetails(showSlug, activeSeason?.cid, stationId)
        .then((resp) => resp.resource.description_long || '')
        .then(setSeasonDescription)
        .catch(handleSeasonDetailsError)
        .finally(() => {
          setSeasonDescription((prevDescription) => prevDescription || '')
          setLoadingDescription(false)
        })
    }
  }, [activeSeason])

  const getExtras = () =>
    getExtrasInSeasons(
      showSlug,
      activeSeason?.cid,
      stationId,
      '', // TODO: delete this unused param in content actions
      null // TODO: Delete it, several tests made and this param seems to be unused
    )
      .then((resp) => {
        setVideoSections((previousVS) => [
          ...previousVS,
          getSection('Extras', activeSeason, resp.collections[0]?.content),
        ])
      })
      .catch(handleExtrasInSeasonsError)

  const getEpisodes = () =>
    getPaginatedEpisodes(
      showSlug,
      activeSeason?.cid,
      stationId,
      '' // TODO: delete this unused param in content actions
    )
      .then((paginatedEpisodes) => {
        setVideoSections((previousVS) => [
          getSection('Episodes', activeSeason, paginatedEpisodes),
          ...previousVS,
        ])
      })
      .catch(handlePaginatedEpisodesError)

  return {
    videoSections,
    seasonDescription,
    loading: loadingSections || loadingDescription,
  }
}

export default useSeasonContent
